import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import SvgImage from '../SvgImage';
import useHabitat from '../../../hooks/useHabitat';
import { insertUrlParam, getUrlParamValue } from '../../../utilities/browser';
import TabsProps, { TabProps, TabPanelProps } from './type';
import './styles.scss';
import useTwitter from '../../../hooks/useTwitter';

const TabPanel = ({
  children,
  tabId,
}: TabPanelProps) => {
  useTwitter('https://platform.twitter.com/widgets.js');
  useHabitat();

  return (
    <div
      role="tabpanel"
      tabIndex={0}
      aria-labelledby={`tab-${tabId}`}
    >
      {children}
    </div>
  );
};

const Tab = React.forwardRef(({
  id,
  label,
  date,
  selected,
  ...props
}: TabProps, ref: any) => (
  <button
    id={id}
    role="tab"
    type="button"
    tabIndex={selected ? 0 : -1}
    aria-selected={selected}
    className={selected ? 'tabs__button tabs__button--selected' : 'tabs__button'}
    {...props}
    ref={ref}
  >
    {label}
    {date && (
      <span className="tabs__button-date">{date}</span>
    )}
  </button>
));

const Tabs = ({
  ariaLabel,
  discipline,
  nested,
  children,
  changeTabCallBack,
  isCalendar,
  isEvent = false,
  withScrollPaddles,
}: TabsProps) => {
  const [previousIsDisabled, setPreviousIsDisabled] = useState(true);
  const [nextIsDisabled, setNextIsDisabled] = useState(false);
  const [scrollPaddlesShowing, setScrollPaddlesShowing] = useState(false);
  const tabScrollContainer = useRef<HTMLDivElement>(null);
  const tabScrollContent = useRef<HTMLDivElement>(null);
  const tabPaddles = useRef<HTMLDivElement>(null);

  const root = 'tabs';
  const classes = [
    root,
    `${root}--${discipline}`,
  ];
  if (nested) {
    classes.push(`${root}--nested`);
  }
  if (isCalendar) {
    classes.push(`${root}--calendar`);
  }
  if (isEvent) {
    classes.push(`${root}--event`);
  }

  // @ts-ignore
  const tabs = children?.flat()
    .filter((child: JSX.Element) => child.type.displayName === 'Tab');
  // @ts-ignore
  const tabPanels = children?.flat()
    .filter((child: JSX.Element) => child.type.displayName === 'TabPanel');

  const getLoadingSelectedTabIndex = () => {
    if (!nested) {
      const urlSlug = getUrlParamValue('tab');

      const selected = tabs.find((tab: JSX.Element) => tab.props.id === urlSlug)
        || tabs.find((tab: JSX.Element) => tab.props.selected === true);

      return selected ? tabs.indexOf(selected) : 0;
    }

    return 0;
  };

  const [selectedTab, setSelectedTab] = useState<number>(getLoadingSelectedTabIndex);
  const tabsRef = useRef<HTMLButtonElement[]>([]);

  const selectTab = (index: number): void => {
    setSelectedTab(index);

    if (!nested) {
      insertUrlParam('tab', tabs[index].props.id);
    }
  };

  const navigateTabs = (
    event: React.KeyboardEvent,
    tabRefs: HTMLButtonElement[],
  ): void => {
    if (event.key === 'ArrowLeft') {
      const prevIndex = tabRefs
        .findIndex((tab) => tab === document.activeElement) - 1;
      const modulo = tabRefs.length;
      const prevTabIndex = ((prevIndex % modulo) + modulo) % modulo;

      tabRefs[prevTabIndex].focus();
    } else if (event.key === 'ArrowRight') {
      const nextIndex = tabRefs
        .findIndex((tab) => tab === document.activeElement) + 1;
      const modulo = tabRefs.length;
      const nextTabIndex = nextIndex % modulo;

      tabRefs[nextTabIndex].focus();
    }
  };

  const handlePaddleOnClick = (direction: 'previous' | 'next') => {
    if (tabScrollContainer.current && tabScrollContent.current) {
      const scrollIncrement = 400;
      let scrollValue = tabScrollContainer.current.scrollLeft;
      if (direction === 'previous') {
        setNextIsDisabled(false);
        scrollValue -= scrollIncrement;
      }
      if (direction === 'next') {
        setPreviousIsDisabled(false);
        scrollValue += scrollIncrement;
      }
      tabScrollContainer.current.scroll({
        left: scrollValue,
        behavior: 'smooth',
      });

      if (scrollValue < 1) {
        setPreviousIsDisabled(true);
      }
      if (scrollValue >= tabScrollContent.current.scrollWidth - window.innerWidth) {
        setNextIsDisabled(true);
      }
    }
  };

  useEffect(() => {
    if (tabScrollContainer.current && tabScrollContent.current) {
      if (tabScrollContent.current.scrollWidth > tabScrollContainer.current.offsetWidth) {
        setScrollPaddlesShowing(true);
      }
    }
  }, []);

  const tabsWithDate = tabs.some((tab: JSX.Element) => !!tab.props.date);

  return (
    <div className={classes.join(' ')}>
      <div className={`${root}__buttons-wrapper ${isCalendar ? 'container' : ''}`}>
        <div
          ref={tabScrollContainer}
          className={`tabs__buttons-container
          ${withScrollPaddles && scrollPaddlesShowing ? 'tabs__buttons-container--with-scroll-paddles' : ''}`}
        >
          <div
            ref={tabScrollContent}
            className={`tabs__buttons ${tabsWithDate ? 'tabs__buttons--has-date' : ''}`}
            role="tablist"
            aria-label={ariaLabel}
          >
            {tabs && tabs.map((tab: JSX.Element, index: number) => (
              <Tab
                key={tab.props.id}
                ref={(element: HTMLButtonElement) => {
                  tabsRef.current[index] = element;
                }}
                id={`tab-${tab.props.id}`}
                label={tab.props.label}
                date={tab.props.date}
                selected={index === selectedTab}
                onClick={() => {
                  selectTab(index);
                  if (changeTabCallBack) {
                    changeTabCallBack();
                  }
                }}
                onKeyDown={(event) => navigateTabs(event, tabsRef.current)}
              />
            ))}
          </div>
        </div>
        {withScrollPaddles && scrollPaddlesShowing && (
          <div ref={tabPaddles} className="tabs__controls">
            <button
              type="button"
              className="tabs__controls--previous-slide"
              disabled={previousIsDisabled}
              onClick={() => handlePaddleOnClick('previous')}
              aria-label="previous"
            >
              <SvgImage ariaHidden reference="#icon-right-paddle" />
            </button>
            <button
              type="button"
              className="tabs__controls--next-slide"
              disabled={nextIsDisabled}
              onClick={() => handlePaddleOnClick('next')}
              aria-label="next"
            >
              <SvgImage ariaHidden reference="#icon-right-paddle" />
            </button>
          </div>
        )}

      </div>
      {tabPanels && tabPanels
        .find((panel: JSX.Element) => tabs[selectedTab].props.id === panel.props.tabId)}
    </div>
  );
};

Tab.displayName = 'Tab';
TabPanel.displayName = 'TabPanel';

export { Tab, TabPanel };
export default Tabs;
